import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useManualBooking } from '../../../context/ManualBookingContext';
import { useAuth } from '../../../../context/AuthContext';
import { formatCurrency } from '../../../../utils/helpers';
import { filterServicesByLocation } from '../../../../locations/filterByLocation';
import GradientButton from '../../../../components/buttons/GradientButton';
import ImagePreview from '../../../../shared/components/ImagePreview';
import { 
  isTaxEnabled, 
  getTaxRate, 
  shouldTaxAddons,
  calculateTaxAmount
} from '../../../../utils/tax/taxCalculator';
import { listCompanyRecordsAuto } from '../../../../api/companyRecords';
import ManualBookingEmptyServices from '../empty-services/ManualBookingEmptyServices';

const ServiceStep = ({ onNext, onBack }) => {
  const [services, setServices] = useState([]);
  const [emptyReason, setEmptyReason] = useState('none');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { companyData } = useAuth();
  const { updateBookingData, bookingData } = useManualBooking();

  useEffect(() => {
    const fetchServices = async () => {
      if (!companyData?.id) {
        setError('Company data is missing. Please ensure you are properly logged in.');
        setLoading(false);
        return;
      }

      try {
        const snapshot = await listCompanyRecordsAuto({
          companyId: companyData.id,
          kind: 'services',
        });

        const activeServices = snapshot.filter((service) => service.active !== false);
        const bookableServices = activeServices.filter((service) => (
          Array.isArray(service.employeeIds) && service.employeeIds.length > 0
        ));

        const servicesData = bookableServices
          .map(service => {
            const serviceData = service;

            // Process packages to include selected add-ons and other details
            const packages = (serviceData.packages || []).map(pkg => ({
              id: pkg.id,
              name: pkg.name,
              description: pkg.description || '',
              price: pkg.price || 0,
              duration: pkg.duration || serviceData.duration,
              bufferTime: pkg.bufferTime || 0,
              sessions: pkg.sessions || 1,
              validity: pkg.validity || null,
              terms: pkg.terms || '',
              imageUrl: pkg.imageUrl || '',
              discount: pkg.discount || 0,
              selectedAddons: (pkg.selectedAddons || []).map(addon => ({
                id: addon.id,
                name: addon.name,
                duration: addon.duration || 0,
                price: addon.price || 0,
                imageUrl: addon.imageUrl || ''
              }))
            }));

            // Calculate tax if tax settings exist
            const taxSettings = serviceData.taxSettings || {};
            
            // Use tax utility functions
            const taxEnabled = isTaxEnabled(taxSettings);
            const taxRate = getTaxRate(taxSettings);
            const basePrice = parseFloat(serviceData.price) || 0;
            
            // Calculate initial tax amount on base price only
            const initialTaxAmount = taxEnabled ? calculateTaxAmount(basePrice, taxRate) : 0;

            return {
              id: service.id,
              ...serviceData,
              name: serviceData.name,
              price: basePrice,
              duration: serviceData.duration || 30,
              description: serviceData.description,
              addons: serviceData.addOns || [],
              packages,
              depositSettings: serviceData.depositSettings || null,
              // Add tax information
              taxSettings: {
                ...taxSettings,
                enabled: taxEnabled,
                rate: taxRate,
                serviceRate: taxSettings.serviceRate,
                includeAddons: shouldTaxAddons(taxSettings)
              },
              taxRate: taxRate,
              taxAmount: initialTaxAmount,
              taxBreakdown: {
                basePrice: basePrice,
                taxableAmount: basePrice,
                taxRate: taxRate,
                taxAmount: initialTaxAmount,
                total: basePrice + initialTaxAmount
              }
            };
          });

        const locationFilteredServices = filterServicesByLocation(
          servicesData,
          bookingData.locationId
        );
        setServices(locationFilteredServices);
        if (activeServices.length === 0) {
          setEmptyReason('none');
        } else if (bookableServices.length === 0) {
          setEmptyReason('no-staff');
        } else if (locationFilteredServices.length === 0) {
          setEmptyReason('location');
        } else {
          setEmptyReason('none');
        }
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Failed to load services');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [companyData?.id, bookingData.locationId]);

  const handleServiceSelect = (service) => {
    // Create a clean service object with all required fields
    const selectedService = {
      // Basic service info
      id: service.id,
      name: service.name || '',
      duration: parseInt(service.duration) || 60,
      price: service.price || 0,
      description: service.description || '',
      bufferTime: parseInt(service.bufferTime) || 0,
      
      // Tax information
      taxSettings: service.taxSettings || null,
      taxRate: service.taxRate || 0,
      taxAmount: service.taxAmount || 0,
      taxBreakdown: service.taxBreakdown || null,
      
      // Include original fields to maintain data consistency
      addOns: service.addOns || [],
      addons: service.addons || [],
      packages: service.packages || [],
      imageUrl: service.imageUrl || '',
      active: service.active !== false,
      employeeIds: Array.isArray(service.employeeIds) ? service.employeeIds : [],
      createdAt: service.createdAt || new Date().toISOString(),
      updatedAt: service.updatedAt || new Date().toISOString(),
      depositSettings: service.depositSettings || null,
    };

    // Update booking data with the selected service
    // We'll handle addons and packages in the next step
    const bookingUpdate = {
      selectedService,
      // Add service fields at root level for easier access
      serviceId: selectedService.id,
      serviceName: selectedService.name,
      serviceDuration: selectedService.duration,
      servicePrice: selectedService.price, // Base price, will be updated in next step
      serviceDescription: selectedService.description,
      // Reset selected options - will be set in next step
      selectedAddons: [],
      selectedPackage: null,
      // Reset datetime when service changes
      selectedDateTime: null
    };

    updateBookingData(bookingUpdate);
    onNext();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 1 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (services.length === 0) {
    return <ManualBookingEmptyServices reason={emptyReason} />;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {services.map((service) => {
          const hasOptions = (service.addons?.length > 0 || service.packages?.length > 0);
          const optionsCount = (service.addons?.length || 0) + (service.packages?.length || 0);
          
          return (
            <Grid item xs={6} sm={4} md={3} key={service.id}>
              <Box
                onClick={() => handleServiceSelect(service)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease-in-out',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 2,
                  '&:hover': {
                    '& > div:first-of-type': {
                      borderColor: theme.palette.primary.main,
                      transform: 'scale(1.05)',
                    },
                  },
                }}
              >
                {/* Round Image */}
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    mb: 2,
                    border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  {service.imageUrl ? (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        '& img': {
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        },
                      }}
                    >
                      <ImagePreview
                        imageUrl={service.imageUrl}
                        alt={service.name}
                        height={120}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        {service.name?.charAt(0)?.toUpperCase() || 'S'}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Service Name */}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 0.5,
                    color: 'text.primary',
                    wordBreak: 'break-word',
                  }}
                >
                  {service.name}
                </Typography>

                {/* Price */}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    color: 'success.main',
                  }}
                >
                  {formatCurrency(service.price)}
                </Typography>

                {/* Options Available Text */}
                {hasOptions && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'primary.main',
                      fontWeight: 500,
                    }}
                  >
                    {optionsCount} {optionsCount === 1 ? 'option' : 'options'} available
                  </Typography>
                )}

                {!hasOptions && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontStyle: 'italic',
                    }}
                  >
                    No options
                  </Typography>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box 
        sx={{ 
          display: 'flex',
          justifyContent: 'center',
          mt: 4,
          pt: 3,
        }}
      >
        <GradientButton
          onClick={onBack}
          variant="primary"
        >
          Back
        </GradientButton>
      </Box>
    </Box>
  );
};

export default ServiceStep; 